import React, { useState, useEffect } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";

const ResellerTiers = () => {
  const { user, loading: authLoading } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Check if user is admin or reseller
  const isAdmin = user?.role === "admin";
  const isReseller = user?.type === "Reseller";

  useEffect(() => {
    fetchTiers();
  }, []);

  // Redirect if user is not admin or reseller (after hooks)
  if (!authLoading && !isAdmin && !isReseller) {
    return <Navigate to="/dashboard" />;
  }

  const fetchTiers = async () => {
    setLoading(true);
    try {
      const response = await apiService.getResellerTiers();
      const tiersData = response.data?.tiers || [];

      setTiers(tiersData);
    } catch (error) {
      console.error("Error fetching reseller tiers:", error);
      toast.error(error.message || "Failed to load reseller tiers");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (tier) => {
    setEditingTier(tier);
    setEditValues({
      tierName: tier.tierName,
      tierSlug: tier.tierSlug,
      maxUsers: tier.maxUsers,
      includedKeys: tier.includedKeys,
      cost: tier.cost,
      costPerKey: tier.costPerKey,
      discountPercentage: tier.discountPercentage,
      tierOrder: tier.tierOrder,
      isActive: tier.isActive,
    });
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingTier(null);
    setEditValues({});
  };

  const handleSave = async () => {
    if (!editingTier) return;

    setIsSaving(true);
    try {
      await apiService.updateResellerTier(editingTier.id, editValues);
      toast.success("Tier updated successfully");
      handleCloseModal();
      await fetchTiers();
    } catch (error) {
      console.error("Error updating tier:", error);
      toast.error(error.message || "Failed to update tier");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatCurrency = (value) => `$${parseFloat(value).toFixed(2)}`;

  const getTierBadgeColor = (tierSlug) => {
    const colors = {
      nano: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800",
      starter:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800",
      basic:
        "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800",
      plus: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800",
      growth:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800",
      pro: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800",
      scale:
        "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800",
      "enterprise-1":
        "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400 border-pink-200 dark:border-pink-800",
      "enterprise-2":
        "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-400 border-fuchsia-200 dark:border-fuchsia-800",
      "enterprise-3":
        "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400 border-violet-200 dark:border-violet-800",
      "enterprise-4":
        "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800",
    };

    return (
      colors[tierSlug?.toLowerCase()] ||
      "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-600"
    );
  };

  return (
    <div className="w-full space-y-4 sm:space-y-6">
      {/* Reseller Disclaimer - Only show for resellers */}
      {isReseller && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400 rounded-lg p-4 shadow-sm">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="fas fa-info-circle text-blue-500 dark:text-blue-400 text-xl mt-0.5" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1">
                Automatic Tier Adjustments
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-200 mb-2">
                Your reseller tier may be automatically upgraded or downgraded
                as your customer base grows or shrinks, in order to ensure fair
                pricing and accurate tier alignment. This adjustment happens
                seamlessly and is based on the number of active LunaKeys™ you
                maintain, so your account always reflects your current level of
                activity.
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-200 font-medium mb-2">
                <i className="fas fa-shopping-cart mr-1.5" />
                You can always buy below your tier but not above.
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 italic">
                <i className="fas fa-exclamation-triangle mr-1" />
                This is subject to change at any time.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tiers Table */}
      <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-md border border-gray-100 dark:border-gray-700/50">
        <div
          className="overflow-x-auto"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  Tier Name
                </th>
                {isAdmin && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Slug
                  </th>
                )}
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  Max Users
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  Included Keys
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  Cost
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  Cost/Key
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  Discount %
                </th>
                {isAdmin && (
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Order
                  </th>
                )}
                {isAdmin && (
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                )}
                {isAdmin && (
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td className="text-center py-12" colSpan={isAdmin ? 10 : 7}>
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                      <span className="text-gray-500">Loading tiers...</span>
                    </div>
                  </td>
                </tr>
              ) : tiers.length === 0 ? (
                <tr>
                  <td
                    className="text-center py-12 text-gray-500"
                    colSpan={isAdmin ? 10 : 7}
                  >
                    No tiers found
                  </td>
                </tr>
              ) : (
                tiers.map((tier, index) => (
                  <motion.tr
                    animate={{ opacity: 1 }}
                    className={`${index % 2 === 0 ? "table-row-striped-even" : "table-row-striped-odd"} table-row-hover`}
                    initial={{ opacity: 0 }}
                    key={tier.id}
                    transition={{ delay: index * 0.05 }}
                  >
                    {/* Tier Name */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-full border ${getTierBadgeColor(
                          tier.tierSlug
                        )}`}
                      >
                        {tier.tierName}
                      </span>
                    </td>

                    {/* Slug - Admin only */}
                    {isAdmin && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                          {tier.tierSlug}
                        </div>
                      </td>
                    )}

                    {/* Max Users */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {tier.maxUsers.toLocaleString()}
                      </div>
                    </td>

                    {/* Included Keys */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {tier.includedKeys.toLocaleString()}
                      </div>
                    </td>

                    {/* Cost */}
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatCurrency(tier.cost)}
                      </div>
                    </td>

                    {/* Cost Per Key */}
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {formatCurrency(tier.costPerKey)}
                      </div>
                    </td>

                    {/* Discount % */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {tier.discountPercentage.toFixed(2)}%
                      </div>
                    </td>

                    {/* Order - Admin only */}
                    {isAdmin && (
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {tier.tierOrder}
                        </div>
                      </td>
                    )}

                    {/* Status - Admin only */}
                    {isAdmin && (
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            tier.isActive
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400"
                          }`}
                        >
                          {tier.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                    )}

                    {/* Actions - Admin only */}
                    {isAdmin && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          <button
                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            onClick={() => handleOpenEdit(tier)}
                          >
                            <i className="fas fa-edit text-sm" />
                          </button>
                        </div>
                      </td>
                    )}
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal - Admin only */}
      {isAdmin && (
        <AnimatePresence>
          {showEditModal && editingTier && (
            <motion.div
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              onClick={handleCloseModal}
            >
              <motion.div
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                exit={{ scale: 0.9, opacity: 0 }}
                initial={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Edit Tier:
                    </h2>
                    <span
                      className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-full border ${getTierBadgeColor(
                        editingTier.tierSlug
                      )}`}
                    >
                      {editingTier.tierName}
                    </span>
                  </div>
                  <button
                    className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    onClick={handleCloseModal}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Tier Name & Slug */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Tier Name</label>
                      <input
                        className="input w-full"
                        type="text"
                        value={editValues.tierName}
                        onChange={(e) =>
                          handleInputChange("tierName", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label className="label">Tier Slug</label>
                      <input
                        className="input w-full font-mono"
                        type="text"
                        value={editValues.tierSlug}
                        onChange={(e) =>
                          handleInputChange("tierSlug", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  {/* Max Users & Included Keys */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Max Users</label>
                      <input
                        className="input w-full"
                        min="1"
                        type="number"
                        value={editValues.maxUsers}
                        onChange={(e) =>
                          handleInputChange(
                            "maxUsers",
                            parseInt(e.target.value)
                          )
                        }
                      />
                    </div>
                    <div>
                      <label className="label">Included Keys</label>
                      <input
                        className="input w-full"
                        min="1"
                        type="number"
                        value={editValues.includedKeys}
                        onChange={(e) =>
                          handleInputChange(
                            "includedKeys",
                            parseInt(e.target.value)
                          )
                        }
                      />
                    </div>
                  </div>

                  {/* Cost & Cost Per Key */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Cost ($)</label>
                      <input
                        className="input w-full"
                        min="0"
                        step="0.01"
                        type="number"
                        value={editValues.cost}
                        onChange={(e) =>
                          handleInputChange("cost", parseFloat(e.target.value))
                        }
                      />
                    </div>
                    <div>
                      <label className="label">Cost Per Key ($)</label>
                      <input
                        className="input w-full"
                        min="0"
                        step="0.01"
                        type="number"
                        value={editValues.costPerKey}
                        onChange={(e) =>
                          handleInputChange(
                            "costPerKey",
                            parseFloat(e.target.value)
                          )
                        }
                      />
                    </div>
                  </div>

                  {/* Discount % & Tier Order */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Discount Percentage (%)</label>
                      <input
                        className="input w-full"
                        max="100"
                        min="0"
                        step="0.01"
                        type="number"
                        value={editValues.discountPercentage}
                        onChange={(e) =>
                          handleInputChange(
                            "discountPercentage",
                            parseFloat(e.target.value)
                          )
                        }
                      />
                    </div>
                    <div>
                      <label className="label">Tier Order</label>
                      <input
                        className="input w-full"
                        min="1"
                        type="number"
                        value={editValues.tierOrder}
                        onChange={(e) =>
                          handleInputChange(
                            "tierOrder",
                            parseInt(e.target.value)
                          )
                        }
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="label">Status</label>
                    <select
                      className="input w-full"
                      value={editValues.isActive ? "active" : "inactive"}
                      onChange={(e) =>
                        handleInputChange(
                          "isActive",
                          e.target.value === "active"
                        )
                      }
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    className="btn btn-ghost flex-1 justify-center"
                    disabled={isSaving}
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary flex-1 justify-center"
                    disabled={isSaving}
                    onClick={handleSave}
                  >
                    {isSaving ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default ResellerTiers;
